import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { grammarService, type GrammarLesson, type GrammarProgress } from "@/services/grammarService";
import { normalizeGrammarAnswer } from "@/utils/textUtils";
import { flashcardService } from "@/services/flashcardService";
import { wordBankService } from "@/services/wordBankService";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  BookOpen, 
  ArrowLeft, 
  CheckCircle2, 
  XCircle, 
  Lightbulb, 
  ChevronRight, 
  AlertTriangle,
  Award,
  Sparkles,
  HelpCircle,
  Loader2,
  Languages,
  Bookmark
} from "lucide-react";

const getGenderEmoji = (name: string) => {
  const n = (name || "").toLowerCase().trim();
  const femaleList = ['alice', 'marie', 'sophie', 'sylvie', 'julie', 'camille', 'claire', 'lucie', 'emilie', 'celine', 'isabelle', 'jeanne', 'nathalie', 'femme', 'fille', 'cliente', 'vendeuse', 'maman', 'mère'];
  const maleList = ['thomas', 'jean', 'paul', 'pierre', 'luc', 'marc', 'nicolas', 'julien', 'alexandre', 'guillaume', 'homme', 'garçon', 'client', 'vendeur', 'papa', 'père'];
  
  if (femaleList.some(f => n.includes(f))) return "👩";
  if (maleList.some(m => n.includes(m))) return "👨";
  
  if (n.endsWith('a') || n.endsWith('ine') || n.endsWith('elle') || n.endsWith('ette') || n.endsWith('ie')) {
    return "👩";
  }
  return "👨";
};

export default function GrammarLessonView() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");

  const [lesson, setLesson] = useState<GrammarLesson | null>(null);
  const [nextLesson, setNextLesson] = useState<GrammarLesson | null>(null);
  const [progress, setProgress] = useState<GrammarProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Exercise responses state
  const [selectedOptions, setSelectedOptions] = useState<{ [exerciseIdx: number]: string }>({});
  const [checkedExercises, setCheckedExercises] = useState<{ [exerciseIdx: number]: boolean }>({});

  const [flippedCards, setFlippedCards] = useState<{ [cardIdx: number]: boolean }>({});
  const [scrollProgress, setScrollProgress] = useState(0);

  const [savingCards, setSavingCards] = useState<{ [cardIdx: number]: boolean }>({});
  const [savedCards, setSavedCards] = useState<{ [cardIdx: number]: boolean }>({});
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  
  const [selectionText, setSelectionText] = useState("");
  const [selectionRect, setSelectionRect] = useState<DOMRect | null>(null);
  const [isSavingWord, setIsSavingWord] = useState(false);

  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const handleSaveFlashcard = async (fc: any, fcIdx: number) => {
    if (savedCards[fcIdx] || savingCards[fcIdx]) return;
    try {
      setSavingCards(prev => ({ ...prev, [fcIdx]: true }));
      
      const payload = {
        front: fc.front,
        back: fc.back,
        category: fc.category || "Grammaire",
        difficulty: fc.difficulty || "medium",
        type: "grammar_lesson",
        source: "grammar_lesson",
        language: "fr",
        tags: [lesson?.title || "Gramática"],
        metadata: {
          lessonId: lesson?.id,
          lessonTitle: lesson?.title,
          category: fc.category || "Grammaire"
        }
      };

      await flashcardService.createFlashcard(payload);
      setSavedCards(prev => ({ ...prev, [fcIdx]: true }));
      showToast("Flashcard adicionado ao seu banco!");
    } catch (err: any) {
      console.error("Error saving flashcard:", err);
      if (err.message && (err.message.includes("já existe") || err.message.includes("duplicate"))) {
        setSavedCards(prev => ({ ...prev, [fcIdx]: true }));
        showToast("Este flashcard já está no seu banco.");
      } else {
        showToast("Erro ao salvar o flashcard.");
      }
    } finally {
      setSavingCards(prev => ({ ...prev, [fcIdx]: false }));
    }
  };

  const handleSelection = () => {
    setTimeout(() => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
        setSelectionRect(null);
        setSelectionText("");
        return;
      }
      const text = selection.toString().trim();
      if (text.length > 0 && text.length < 400) { // Limit length to avoid huge selections
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        setSelectionText(text);
        setSelectionRect(rect);
      } else {
        setSelectionRect(null);
        setSelectionText("");
      }
    }, 10);
  };

  const handleSaveWord = async () => {
    if (!selectionText || isSavingWord) return;
    try {
      setIsSavingWord(true);
      await wordBankService.createWord({
        term: selectionText,
        meaning: "Salvo via lição de gramática",
        context: lesson?.title || "",
        category: "Vocabulário"
      });
      showToast("Palavra salva no seu banco!");
      setSelectionRect(null);
      setSelectionText("");
      window.getSelection()?.removeAllRanges();
    } catch (err: any) {
      console.error("Error saving word:", err);
      if (err.message && (err.message.includes("já está cadastrada") || err.message.includes("duplicate"))) {
        showToast("Esta palavra já está no seu banco.");
      } else {
        showToast("Erro ao salvar palavra.");
      }
      setSelectionRect(null);
      setSelectionText("");
      window.getSelection()?.removeAllRanges();
    } finally {
      setIsSavingWord(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalHeight > 0) {
        const pct = (window.pageYOffset / totalHeight) * 100;
        setScrollProgress(pct);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    async function loadLessonData() {
      if (!slug) return;
      try {
        setLoading(true);
        const fetchedLesson = await grammarService.getLessonBySlug(slug);
        if (!fetchedLesson) {
          setLoading(false);
          return;
        }
        setLesson(fetchedLesson);

        // Fetch user progress and lessons to locate the next lesson
        const [allLessons, userProgress] = await Promise.all([
          grammarService.fetchLessons(),
          grammarService.fetchUserProgress()
        ]);

        // Find progress for current lesson
        const currentProgress = userProgress.find(p => p.lesson_id === fetchedLesson.id);
        if (currentProgress) {
          setProgress(currentProgress);
        }

        // Find next lesson
        const currentIndex = allLessons.findIndex(l => l.id === fetchedLesson.id);
        if (currentIndex !== -1 && currentIndex < allLessons.length - 1) {
          setNextLesson(allLessons[currentIndex + 1]);
        } else {
          setNextLesson(null);
        }

        // Reset or load exercise states
        if (mode === "retry" || !currentProgress?.answers) {
          setSelectedOptions({});
          setCheckedExercises({});
        } else {
          try {
            const answersObj = typeof currentProgress.answers === 'string' 
              ? JSON.parse(currentProgress.answers) 
              : currentProgress.answers;
            
            if (answersObj?.selectedOptions) {
              setSelectedOptions(answersObj.selectedOptions);
            } else {
              setSelectedOptions({});
            }
            if (answersObj?.checkedExercises) {
              setCheckedExercises(answersObj.checkedExercises);
            } else {
              setCheckedExercises({});
            }
          } catch (e) {
            setSelectedOptions({});
            setCheckedExercises({});
          }
        }
        setFlippedCards({});
        setSavedCards({});
        setSavingCards({});
      } catch (err) {
        console.error("Error loading lesson detail:", err);
      } finally {
        setLoading(false);
      }
    }

    loadLessonData();
  }, [slug]);

  const handleSelectOption = (exerciseIdx: number, option: string) => {
    if (checkedExercises[exerciseIdx]) return; // Already checked
    setSelectedOptions(prev => ({ ...prev, [exerciseIdx]: option }));
  };

  const toggleFlipCard = (idx: number) => {
    setFlippedCards(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const handleCheckExercise = (exerciseIdx: number) => {
    if (!selectedOptions[exerciseIdx]) return;
    setCheckedExercises(prev => ({ ...prev, [exerciseIdx]: true }));
  };

  const handleMarkAsCompleted = async () => {
    if (!lesson || !lesson.id) return;
    try {
      setSaving(true);
      
      // Calculate score based on exercises answered correctly
      let correctCount = 0;
      const totalExercises = lesson.exercises?.length || 0;
      if (totalExercises > 0) {
        lesson.exercises.forEach((ex, idx) => {
          const userAns = normalizeGrammarAnswer(selectedOptions[idx]);
          const correctAns = normalizeGrammarAnswer(ex.answer);
          const isCorrect = ex.type === "multiple_choice"
            ? selectedOptions[idx] === ex.answer
            : userAns === correctAns;

          if (isCorrect) {
            correctCount++;
          }
        });
      }
      const scorePercent = totalExercises > 0 ? Math.round((correctCount / totalExercises) * 100) : 100;

      const updatedProgress = await grammarService.updateUserProgress(
        lesson.id,
        "completed",
        scorePercent,
        { selectedOptions, checkedExercises }
      );
      if (updatedProgress) {
        setProgress(updatedProgress);
      }
    } catch (err) {
      console.error("Error completing lesson:", err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-24 text-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
        <h4 className="font-bold text-slate-700 text-base">Carregando conteúdo da lição...</h4>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="py-24 text-center text-slate-500">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h3 className="text-lg font-bold">Lição não encontrada</h3>
        <p className="text-xs mt-1">A lição selecionada pode não estar ativa ou não existe.</p>
        <Button variant="outline" onClick={() => navigate("/grammar")} className="mt-6">
          Voltar para a Trilha
        </Button>
      </div>
    );
  }

  const isCompleted = progress?.status === "completed";
  const exercisesLength = lesson.exercises?.length || 0;
  const allExercisesChecked = Object.keys(checkedExercises).length === exercisesLength;

  const renderHighlightedText = (text: string, isCorrect: boolean) => {
    if (!text) return "";
    if (text.includes("*")) {
      const parts = text.split(/(\*[^*]+\*)/g);
      return (
        <>
          {parts.map((part, index) => {
            if (part.startsWith("*") && part.endsWith("*")) {
              const cleanText = part.slice(1, -1);
              return (
                <span 
                  key={index} 
                  className={
                    isCorrect 
                      ? "text-emerald-700 font-extrabold bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded mx-0.5 shadow-3xs" 
                      : "text-red-700 font-bold line-through decoration-red-500 decoration-2 bg-red-100 border border-red-200 px-1.5 py-0.5 rounded mx-0.5 shadow-3xs"
                  }
                >
                  {cleanText}
                </span>
              );
            }
            return <span key={index}>{part}</span>;
          })}
        </>
      );
    }
    return <span className={isCorrect ? "" : "line-through"}>{text}</span>;
  };

  const renderMarkdownBold = (text: string) => {
    if (!text) return "";
    if (text.includes("**")) {
      const parts = text.split(/(\*\*[^*]+\*\*)/g);
      return (
        <>
          {parts.map((part, index) => {
            if (part.startsWith("**") && part.endsWith("**")) {
              const cleanText = part.slice(2, -2);
              return (
                <strong key={index} className="font-extrabold text-slate-950">
                  {cleanText}
                </strong>
              );
            }
            return <span key={index}>{part}</span>;
          })}
        </>
      );
    }
    return <span>{text}</span>;
  };

  return (
    <div 
      className="max-w-5xl mx-auto space-y-12 md:space-y-16 pb-24 animate-in fade-in duration-500 text-left leading-relaxed text-slate-700"
      onMouseUp={handleSelection}
      onTouchEnd={handleSelection}
    >
      <style>{`
        .flip-card-container {
          perspective: 1000px;
        }
        .flip-card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          text-align: center;
          transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          transform-style: preserve-3d;
          cursor: pointer;
        }
        .flip-card-inner.flipped {
          transform: rotateY(180deg);
        }
        .flip-card-front, .flip-card-back {
          position: absolute;
          width: 100%;
          height: 100%;
          -webkit-backface-visibility: hidden;
          backface-visibility: hidden;
          border-radius: 1rem;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        .flip-card-back {
          transform: rotateY(180deg);
        }
      `}</style>

      {/* Sticky Scroll Progress Bar rendered inside document.body to prevent parent transforms from breaking position: fixed */}
      {createPortal(
        <div className="fixed top-0 left-0 w-full h-1.5 bg-slate-100/80 z-[9999] pointer-events-none">
          <div 
            className="h-full bg-gradient-to-r from-primary to-indigo-600 transition-all duration-150 ease-out"
            style={{ width: `${scrollProgress}%` }}
          />
        </div>,
        document.body
      )}

      {/* Word Bank Floating Button */}
      {selectionRect && selectionText && createPortal(
        <div 
          className="fixed z-[9999] animate-in fade-in zoom-in-95 duration-200"
          style={{
            top: `${Math.max(10, selectionRect.top - 45)}px`,
            left: `${selectionRect.left + (selectionRect.width / 2)}px`,
            transform: 'translateX(-50%)'
          }}
        >
          <Button 
            size="sm" 
            className="rounded-full shadow-lg gap-2 bg-slate-900 hover:bg-slate-800 text-white"
            onClick={handleSaveWord}
            disabled={isSavingWord}
          >
            {isSavingWord ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bookmark className="w-4 h-4" />}
            Salvar palavra
          </Button>
        </div>,
        document.body
      )}

      {/* Header Actions */}
      <div className="flex items-center justify-between border-b border-slate-100 pb-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/grammar")} 
          className="rounded-xl border border-slate-200 text-slate-600 hover:text-slate-900 gap-1.5 h-10 px-4 bg-white shadow-3xs"
        >
          <ArrowLeft className="w-4 h-4 text-slate-400" />
          {t("grammar.backToTrail", "Voltar")}
        </Button>

        {isCompleted && (
          <Badge className="bg-emerald-50 border-emerald-200 text-emerald-700 font-extrabold text-xs px-3 py-1 flex items-center gap-1.5 rounded-full shadow-3xs">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            {t("grammar.completed", "Concluída")}
          </Badge>
        )}
      </div>

      {/* Lesson Curriculum Info */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Badge className={`text-xs font-black uppercase tracking-wider px-3.5 py-1 rounded-full border-none shadow-3xs ${
            lesson.level.startsWith("A") ? "bg-emerald-50 text-emerald-700" :
            lesson.level.startsWith("B1") ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"
          }`}>
            {lesson.level}
          </Badge>
          {lesson.category && (
            <Badge variant="outline" className="text-slate-500 font-black uppercase tracking-wider border-slate-200 px-3.5 py-1 rounded-full shadow-3xs">
              {lesson.category}
            </Badge>
          )}
        </div>

        <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
          {lesson.order_index}. {lesson.title}
        </h1>
        {lesson.description && (
          <p className="text-slate-500 text-sm md:text-base font-semibold leading-relaxed max-w-3xl leading-7">
            {lesson.description}
          </p>
        )}
      </div>

      {/* Dialogue Block */}
      {lesson.dialogue && (
        <Card className="border border-slate-100 rounded-2xl overflow-hidden shadow-sm bg-white">
          <CardHeader className="bg-slate-50/40 border-b border-slate-100/50 p-6">
            <span className="text-[10px] text-primary font-black uppercase tracking-widest">Dialogue & Contexte</span>
            <CardTitle className="text-lg font-black text-slate-800 mt-1">
              {lesson.dialogue.title || "Dialogue"}
            </CardTitle>
            {lesson.dialogue.context && (
              <CardDescription className="text-xs font-semibold text-slate-500 mt-2 italic flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
                Context: {lesson.dialogue.context}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="p-6 space-y-8">
            {/* Lines of dialogue */}
            {lesson.dialogue.lines && lesson.dialogue.lines.length > 0 && (
              <div className="space-y-5 p-4 bg-slate-50/30 rounded-2xl border border-slate-100/50">
                {(() => {
                  const firstSpeaker = lesson.dialogue.characters?.[0] || "";
                  return lesson.dialogue.lines.map((line, lIdx) => {
                    const isLeft = line.speaker === firstSpeaker;
                    const initials = line.speaker ? line.speaker.charAt(0).toUpperCase() : "?";
                    return (
                      <div 
                        key={lIdx} 
                        className={`flex items-start gap-3.5 max-w-[85%] ${
                          isLeft ? "mr-auto" : "ml-auto flex-row-reverse"
                        }`}
                      >
                        {/* Avatar circle */}
                        <div 
                          className={`w-9 h-9 rounded-full flex items-center justify-center text-lg shrink-0 shadow-2xs select-none ${
                            isLeft ? "bg-primary" : "bg-indigo-600"
                          }`}
                          title={line.speaker}
                        >
                          {getGenderEmoji(line.speaker)}
                        </div>
                        
                        {/* Speech bubble */}
                        <div className={`flex flex-col space-y-1 ${isLeft ? "items-start" : "items-end"}`}>
                          <span className="block text-[10px] font-black uppercase text-slate-400 px-1">
                            {line.speaker}
                          </span>
                          <div 
                            className={`px-4 py-3 rounded-2xl text-slate-800 border ${
                              isLeft 
                                ? "bg-primary/5 border-primary/10 rounded-tl-none" 
                                : "bg-slate-100 border-slate-200/60 rounded-tr-none text-left"
                            }`}
                          >
                            <p className="font-semibold text-slate-500 text-sm md:text-[14px] leading-relaxed">
                              "{renderMarkdownBold(line.text)}"
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}

            {/* Vocabulary Notes */}
            {lesson.dialogue.vocabularyNotes && lesson.dialogue.vocabularyNotes.length > 0 && (
              <div className="space-y-3.5 border-t border-slate-100 pt-6">
                <h4 className="font-extrabold text-slate-800 text-xs flex items-center gap-1.5">
                  <Languages className="w-4 h-4 text-primary" />
                  Notes de Vocabulaire (Glossário de Vocabulário)
                </h4>
                <div className="grid gap-3 sm:grid-cols-2">
                  {lesson.dialogue.vocabularyNotes.map((vn, vnIdx) => (
                    <div key={vnIdx} className="p-3.5 rounded-xl border border-slate-100 bg-white flex items-start gap-2 text-xs shadow-3xs">
                      <span className="font-extrabold text-primary font-mono text-[13px] shrink-0">{vn.term}</span>
                      <span className="text-slate-300 font-semibold shrink-0">-</span>
                      <span className="text-slate-500 font-semibold leading-relaxed">{vn.meaning}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Structured Content Panel */}
      <div className="space-y-10 md:space-y-12">
        
        {/* Intro & Core Rule Section */}
        {lesson.content?.intro && (
          <div className="prose max-w-none text-slate-700 font-semibold text-sm md:text-base leading-relaxed leading-7 md:leading-8">
            <p>{lesson.content.intro}</p>
          </div>
        )}

        {/* Rule Box */}
        {lesson.content?.rule && (
          <div className="bg-gradient-to-br from-indigo-50/50 to-blue-50/40 border border-indigo-100 rounded-2xl p-6 md:p-8 shadow-xs">
            <h3 className="font-extrabold text-indigo-950 text-sm md:text-base flex items-center gap-2 mb-4">
              <BookOpen className="w-4.5 h-4.5 text-indigo-600" />
              Règle Grammaticale (Regra Gramatical)
            </h3>
            <p className="text-slate-700 text-xs md:text-sm font-semibold leading-relaxed leading-6 whitespace-pre-wrap">
              {lesson.content.rule}
            </p>
          </div>
        )}

        {/* Structure Box */}
        {lesson.content?.structure && (
          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-6 shadow-3xs">
            <h4 className="font-extrabold text-slate-800 text-xs flex items-center gap-2 mb-3">
              <Award className="w-4.5 h-4.5 text-primary" />
              Structure de la phrase (Estrutura da frase)
            </h4>
            <p className="text-slate-800 text-xs md:text-sm font-bold leading-relaxed whitespace-pre-wrap font-mono bg-white p-4 rounded-xl border border-slate-100 shadow-3xs">
              {lesson.content.structure}
            </p>
          </div>
        )}

        {/* Examples Section */}
        {lesson.content?.examples && lesson.content.examples.length > 0 && (
          <div className="space-y-4">
            <h4 className="font-extrabold text-slate-800 text-sm md:text-base">Exemples d'utilisation (Exemplos de uso)</h4>
            <ul className="grid gap-3">
              {lesson.content.examples.map((example: any, idx) => {
                const isObj = example && typeof example === "object";
                const sentence = isObj ? example.sentence : example;
                const translation = isObj ? example.translation : "";
                const note = isObj ? example.note : "";

                return (
                  <li key={idx} className="flex flex-col gap-3 bg-white border border-slate-100 p-6 rounded-2xl shadow-3xs text-xs md:text-sm">
                    <div className="flex items-start gap-3.5">
                      <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center font-bold text-[10px] shrink-0 mt-0.5 shadow-3xs">
                        {idx + 1}
                      </span>
                      <div className="space-y-1.5">
                        <span className="text-slate-900 font-extrabold leading-relaxed italic block text-sm md:text-[15px]">
                          "{sentence}"
                        </span>
                        {translation && (
                          <span className="text-slate-500 font-semibold block text-xs md:text-sm">
                            {translation}
                          </span>
                        )}
                      </div>
                    </div>
                    {note && (
                      <span className="self-start ml-9 text-[9px] font-black uppercase text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-lg border border-indigo-100">
                        {note}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Common Mistakes Table */}
        {lesson.content?.commonMistakes && lesson.content.commonMistakes.length > 0 && (
          <div className="space-y-4">
            <h4 className="font-extrabold text-slate-800 text-sm md:text-base flex items-center gap-1.5">
              <AlertTriangle className="w-4.5 h-4.5 text-amber-500" />
              {t("grammar.commonMistakes", "Erros Comuns")}
            </h4>
            
            <div className="grid gap-5">
              {lesson.content.commonMistakes.map((item, idx) => {
                const wrongForm = item.wrong || item.incorrect || "";
                return (
                  <div key={idx} className="space-y-2.5">
                    <div className="grid gap-4 sm:grid-cols-2">
                      {/* Wrong form box */}
                      <div className="bg-red-50/30 border border-red-100/50 border-l-4 border-l-red-500 rounded-xl rounded-l-none p-4 flex items-start gap-2.5 shadow-3xs">
                        <span className="rounded bg-red-100 px-2 py-0.5 text-[9px] font-black uppercase text-red-700 h-fit shrink-0">
                          {t("grammar.incorrect", "Incorreto")}
                        </span>
                        <span className="text-red-950 font-mono text-xs md:text-[13px] font-bold leading-relaxed">
                          {renderHighlightedText(wrongForm, false)}
                        </span>
                      </div>

                      {/* Correct form box */}
                      <div className="bg-emerald-50/30 border border-emerald-100/50 border-l-4 border-l-emerald-500 rounded-xl rounded-l-none p-4 flex items-start gap-2.5 shadow-3xs">
                        <span className="rounded bg-emerald-100 px-2 py-0.5 text-[9px] font-black uppercase text-emerald-700 h-fit shrink-0">
                          {t("grammar.correct", "Correto")}
                        </span>
                        <span className="text-emerald-950 font-mono text-xs md:text-[13px] font-extrabold leading-relaxed">
                          {renderHighlightedText(item.correct, true)}
                        </span>
                      </div>
                    </div>

                    {item.explanation && (
                      <p className="text-xs text-slate-500 font-semibold pl-3 border-l-2 border-slate-200 mt-1 leading-relaxed">
                        {item.explanation}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* TEF Exam Tip Box */}
        {lesson.content?.tefTip && (
          <div className="bg-amber-50/60 border border-amber-200/50 rounded-2xl p-6 flex items-start gap-4 shadow-2xs">
            <div className="w-11 h-11 rounded-2xl bg-amber-100 flex items-center justify-center text-amber-600 shrink-0 shadow-3xs transition-transform duration-300 hover:scale-110">
              <Lightbulb className="w-5.5 h-5.5 fill-amber-500/20" />
            </div>
            <div className="space-y-1.5">
              <h4 className="font-black text-amber-900 text-sm tracking-tight">
                {t("grammar.tefTip", "Dica TEF")}
              </h4>
              <p className="text-slate-700 text-xs md:text-sm font-semibold leading-relaxed leading-6">
                {lesson.content.tefTip}
              </p>
            </div>
          </div>
        )}

      </div>

      {/* Interactive Exercises Section */}
      {exercisesLength > 0 && (
        <div className="border-t border-slate-100 pt-8 space-y-6">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-primary" />
            <h3 className="text-lg md:text-xl font-extrabold text-slate-900 tracking-tight">
              {t("grammar.exercises", "Exercícios")}
            </h3>
          </div>

          <div className="grid gap-6">
            {lesson.exercises.map((ex, exIdx) => {
              const selected = selectedOptions[exIdx] || "";
              const isChecked = checkedExercises[exIdx];
              
              const isCorrect = ex.type === "multiple_choice"
                ? selected === ex.answer
                : normalizeGrammarAnswer(selected) === normalizeGrammarAnswer(ex.answer);

              return (
                <Card key={exIdx} className="border border-slate-100 rounded-2xl overflow-hidden shadow-xs bg-white">
                  <CardHeader className="bg-slate-50/40 p-6 border-b border-slate-100/50 flex flex-row items-center justify-between gap-4">
                    <div>
                      <span className="text-[10px] text-primary font-black uppercase tracking-wider">Questão {exIdx + 1}</span>
                      <CardTitle className="text-sm md:text-base font-extrabold text-slate-800 leading-relaxed mt-1">
                        {ex.prompt}
                      </CardTitle>
                    </div>
                    <Badge variant="outline" className="text-[9px] font-black uppercase tracking-wider text-slate-500 border-slate-200 shrink-0 shadow-3xs">
                      {ex.type === "multiple_choice" ? "Múltipla Escolha" : ex.type === "fill_blank" ? "Preencher Lacuna" : "Transformação"}
                    </Badge>
                  </CardHeader>
                  <CardContent className="p-6 space-y-5">
                    {/* Render inputs depending on type */}
                    {ex.type === "multiple_choice" ? (
                      <div className="grid gap-2.5">
                        {ex.options?.map((opt, oIdx) => {
                          const isOptSelected = selected === opt;
                          const isCorrectAnswer = opt === ex.answer;
                          
                          let optStyle = "border-slate-200 text-slate-700 bg-white hover:border-primary hover:bg-slate-50/50";
                          if (isOptSelected && !isChecked) {
                            optStyle = "border-primary bg-primary/5 text-primary shadow-3xs";
                          } else if (isChecked) {
                            if (isCorrectAnswer) {
                              optStyle = "border-emerald-500 bg-emerald-50 text-emerald-800 shadow-3xs";
                            } else if (isOptSelected && !isCorrect) {
                              optStyle = "border-red-500 bg-red-50 text-red-800 opacity-90 shadow-3xs";
                            } else {
                              optStyle = "border-slate-100 opacity-50 text-slate-400 cursor-not-allowed";
                            }
                          }

                          return (
                            <button
                              key={oIdx}
                              onClick={() => handleSelectOption(exIdx, opt)}
                              disabled={isChecked}
                              className={`group w-full text-left p-4 rounded-xl border font-semibold text-xs md:text-sm transition-all duration-200 ease-in-out flex items-center justify-between ${optStyle}`}
                            >
                              <div className="flex items-center gap-3 text-left w-full min-w-0">
                                {/* Custom radio indicator */}
                                <div className={`w-4.5 h-4.5 rounded-full border-2 shrink-0 flex items-center justify-center transition-all duration-200 ${
                                  isOptSelected 
                                    ? (isChecked 
                                        ? (isCorrectAnswer ? "border-emerald-600 bg-emerald-600" : "border-red-600 bg-red-600")
                                        : "border-primary bg-primary") 
                                    : (isChecked && isCorrectAnswer 
                                        ? "border-emerald-600 bg-emerald-600" 
                                        : "border-slate-300 bg-white group-hover:border-primary/50")
                                }`}>
                                  {(isOptSelected || (isChecked && isCorrectAnswer)) && (
                                    <div className="w-1.5 h-1.5 rounded-full bg-white" />
                                  )}
                                </div>
                                <span className="text-slate-900 font-extrabold leading-relaxed text-left break-words w-full">{opt}</span>
                              </div>
                              {isChecked && isCorrectAnswer && (
                                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                              )}
                              {isChecked && isOptSelected && !isCorrect && (
                                <XCircle className="w-4 h-4 text-red-500 shrink-0" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      // fill_blank or transformation
                      <div className="space-y-1.5">
                        <Label htmlFor={`exercise-input-${exIdx}`} className="text-xs font-bold text-slate-500">Sua resposta:</Label>
                        {(() => {
                          let inputStyle = "border-slate-200 text-slate-900 bg-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary focus:border-primary focus:bg-primary/5 focus:text-primary shadow-3xs";
                          if (isChecked) {
                            if (isCorrect) {
                              inputStyle = "border-emerald-500 bg-emerald-50 text-emerald-800 shadow-3xs disabled:opacity-100 disabled:text-emerald-800 disabled:cursor-not-allowed";
                            } else {
                              inputStyle = "border-red-500 bg-red-50 text-red-800 opacity-90 shadow-3xs disabled:opacity-100 disabled:text-red-800 disabled:cursor-not-allowed";
                            }
                          }
                          return (
                            <Input
                              id={`exercise-input-${exIdx}`}
                              type="text"
                              value={selected}
                              onChange={e => handleSelectOption(exIdx, e.target.value)}
                              disabled={isChecked}
                              placeholder={ex.type === "fill_blank" ? "Digite a palavra que preenche a lacuna..." : "Escreva a frase transformada..."}
                              className={`w-full p-4 h-12 rounded-xl border font-semibold text-xs md:text-sm transition-all duration-200 ease-in-out ${inputStyle}`}
                            />
                          );
                        })()}
                      </div>
                    )}

                    {/* Action buttons and feedback */}
                    <div className="flex flex-col gap-3">
                      {!isChecked ? (
                        <Button
                          onClick={() => handleCheckExercise(exIdx)}
                          disabled={!selected.trim()}
                          className="h-10.5 bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs px-5 w-full sm:w-fit shadow-xs transition-all"
                        >
                          {t("grammar.checkAnswer", "Verificar Resposta")}
                        </Button>
                      ) : (
                        <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 text-left animate-in slide-in-from-top-1 duration-200 shadow-3xs">
                          <h5 className={`text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 ${
                            isCorrect ? "text-emerald-700" : "text-red-700"
                          }`}>
                            {isCorrect ? (
                              <>
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                Resposta Correta!
                              </>
                            ) : (
                              <>
                                <XCircle className="w-3.5 h-3.5 text-red-600" />
                                Resposta Incorreta (Resposta correta: "{ex.answer}")
                              </>
                            )}
                          </h5>
                          {ex.explanation && (
                            <p className="text-slate-600 text-xs font-semibold leading-relaxed mt-2 pl-5 border-l-2 border-slate-200">
                              <span className="font-extrabold text-slate-800 mr-1">{t("grammar.explanation", "Explicação")}:</span>
                              {ex.explanation}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Suggested Flashcards */}
      {lesson.flashcards && lesson.flashcards.length > 0 && (
        <div className="border-t border-slate-100 pt-8 space-y-5">
          <div className="flex items-center gap-2">
            <Bookmark className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg md:text-xl font-extrabold text-slate-900 tracking-tight">
              Flashcards sugeridos para memorização
            </h3>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {lesson.flashcards.map((fc, fcIdx) => (
              <div key={fcIdx} className="flip-card-container h-52 w-full">
                <div 
                  className={`flip-card-inner h-full w-full ${flippedCards[fcIdx] ? "flipped" : ""}`}
                  onClick={() => toggleFlipCard(fcIdx)}
                >
                  {/* CARD FRONT */}
                  <div className="flip-card-front p-5 pb-6 border border-indigo-100 bg-gradient-to-br from-white to-indigo-50/20 shadow-sm flex flex-col justify-between">
                    <div className="space-y-2.5 text-left">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="text-[8px] font-black uppercase text-indigo-600 border-indigo-200 rounded px-2 py-0.5 hover:bg-transparent shadow-3xs">
                          {fc.category || "Grammaire"}
                        </Badge>
                        <Badge className={`text-[8px] font-black uppercase px-2 py-0.5 rounded shadow-3xs border-none ${
                          fc.difficulty === "easy" ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" :
                          fc.difficulty === "medium" ? "bg-amber-50 text-amber-700 hover:bg-amber-50" : "bg-rose-50 text-rose-700 hover:bg-rose-50"
                        }`}>
                          {fc.difficulty === "easy" ? "Fácil" : fc.difficulty === "medium" ? "Médio" : "Difícil"}
                        </Badge>
                      </div>
                      <h5 className="font-extrabold text-slate-900 text-sm md:text-base leading-relaxed">{fc.front}</h5>
                    </div>
                    <div className="text-[10px] font-extrabold text-primary flex items-center justify-center gap-1.5 border-t border-slate-100 pt-2.5 mt-auto">
                      <span>Virar card</span>
                      <ChevronRight className="w-3.5 h-3.5 rotate-90" />
                    </div>
                  </div>

                  {/* CARD BACK */}
                  <div className="flip-card-back p-5 pb-6 border border-primary/20 bg-gradient-to-br from-primary/5 to-indigo-50/30 shadow-md flex flex-col justify-between">
                    <div className="space-y-2.5 text-left">
                      <div className="flex items-center justify-between">
                        <Badge className="bg-primary/10 text-primary hover:bg-primary/10 text-[8px] font-black uppercase rounded px-2 py-0.5 border-none shadow-3xs">
                          Resposta
                        </Badge>
                        <Badge className={`text-[8px] font-black uppercase px-2 py-0.5 rounded shadow-3xs border-none ${
                          fc.difficulty === "easy" ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" :
                          fc.difficulty === "medium" ? "bg-amber-50 text-amber-700 hover:bg-amber-50" : "bg-rose-50 text-rose-700 hover:bg-rose-50"
                        }`}>
                          {fc.difficulty === "easy" ? "Fácil" : fc.difficulty === "medium" ? "Médio" : "Difícil"}
                        </Badge>
                      </div>
                      <h5 className="font-extrabold text-slate-950 text-sm md:text-base leading-relaxed italic">{fc.back}</h5>
                    </div>
                    <div className="text-[10px] font-extrabold flex items-center justify-between border-t border-primary/10 pt-2.5 mt-auto">
                      {/* Voltar button */}
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFlipCard(fcIdx);
                        }}
                        className="text-slate-500 hover:text-slate-700 flex items-center gap-1"
                      >
                        <ChevronRight className="w-3.5 h-3.5 -rotate-90" />
                        <span>Voltar</span>
                      </button>

                      {/* Salvar button */}
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveFlashcard(fc, fcIdx);
                        }}
                        disabled={savingCards[fcIdx]}
                        className={`${
                          savedCards[fcIdx] 
                            ? "text-emerald-600 hover:text-emerald-700" 
                            : "text-primary hover:text-primary/80"
                        } flex items-center gap-1 transition-colors`}
                      >
                        {savingCards[fcIdx] ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : savedCards[fcIdx] ? (
                          <CheckCircle2 className="w-3.5 h-3.5 fill-emerald-100" />
                        ) : (
                          <Bookmark className="w-3.5 h-3.5 fill-primary/10" />
                        )}
                        <span>{savedCards[fcIdx] ? "Salvo" : "Salvar"}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completion & Next Lesson Footer Actions */}
      <div className="border-t border-slate-100 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-center sm:text-left">
          {isCompleted && (
            <p className="text-emerald-600 font-extrabold text-xs flex items-center justify-center sm:justify-start gap-1">
              <Sparkles className="w-4 h-4 fill-current animate-pulse" />
              {t("grammar.lessonCompleted", "Você concluiu esta lição!")}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-3 w-full sm:w-auto justify-end">
          <Button
            onClick={async () => {
              await handleMarkAsCompleted();
              navigate("/grammar");
            }}
            disabled={saving}
            className="h-11 bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs px-6 shadow-md transition-all gap-1.5"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                {t("grammar.finishLesson", "Finalizar Lição")}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Floating Toast Notification */}
      {toastMessage && createPortal(
        <div className="fixed bottom-6 right-6 z-[99999] bg-slate-900 text-white px-4 py-3 rounded-xl shadow-lg animate-in fade-in slide-in-from-bottom-5 duration-300 flex items-center gap-2 max-w-sm text-xs font-bold border border-slate-800">
          <span className="flex-1 text-left">{toastMessage}</span>
          <button onClick={() => setToastMessage(null)} className="text-slate-400 hover:text-white shrink-0 font-normal text-sm px-1">
            &times;
          </button>
        </div>,
        document.body
      )}

    </div>
  );
}
